import React, { useMemo, useCallback } from 'react';
import { DashboardAPIResponse } from 'src/pages/dashboard/Dashboard.interface';
import { xTickFormatter, yTickFormatter, MIN_WIDTH_PER_WORKFLOW } from 'src/components/dashboard/DashboardConstants';
import { collectStatuses, buildStatusBarChartData } from 'src/components/dashboard/DashboardUtils';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SNOW_DATA_SERVICE_ENDPOINT } from 'src/config';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import BarChart from '@amzn/awsui-components-console/bar-chart';
import Button from '@amzn/awsui-components-console/button';
import EmptyState from 'src/components/commonComponents/EmptyState/EmptyState';

interface UserAllWorkflowsChartProps {
	year: string;
	userId: string;
	month?: string;
	workflowNameMap?: Record<string, string>;
}

export default function UserAllWorkflowsChart({ year, userId, month, workflowNameMap }: UserAllWorkflowsChartProps) {
	const getFullName = useCallback((id: string) => workflowNameMap?.[id] || id, [workflowNameMap]);

	const queryParams = useMemo(() => (
		{ year, userId, ...(month && { month }) }
	), [year, userId, month]);

	const { isLoading, data, error, fetchData } = useFetchForSDS<DashboardAPIResponse>({
		path: '/dashboard', method: 'GET', queryParams, url: SNOW_DATA_SERVICE_ENDPOINT,
	});

	const items = data?.items ?? [];
	const allStatuses = useMemo(() => collectStatuses(items), [items]);
	const { chartSeries, xDomain } = useMemo(() => ({
		chartSeries: buildStatusBarChartData(items, allStatuses, getFullName),
		xDomain: items.map((w) => getFullName(w.workflowType)),
	}), [items, allStatuses, getFullName]);

	if (isLoading) return <Box textAlign="center" padding={{ vertical: 'xxl' }}><Spinner size="large" /></Box>;
	if (error) return <Alert type="error" header="Error loading data" action={<Button onClick={() => fetchData()}>Retry</Button>}>{error}</Alert>;

	return (
		<div style={{ overflowX: 'auto', height: '100%', minWidth: `${items.length * MIN_WIDTH_PER_WORKFLOW}px` }}>
			<BarChart
				series={chartSeries}
				xDomain={xDomain}
				yTitle="Count" xTitle="Workflow Type"
				stackedBars fitHeight hideFilter emphasizeBaselineAxis
				detailPopoverSize="large"
				i18nStrings={{ xTickFormatter, yTickFormatter }}
				ariaLabel="User workflow status distribution"
				empty={<EmptyState title="No workflow activity" description={`No activity for ${userId} in ${month ? `${year}-${month}` : year}.`} />}
			/>
		</div>
	);
}
